import React from "react";
import images from "../data/images.json";

/** Normalises "img/foo.jpg" and "/img/foo.jpg" to the manifest key form. */
const key = (src) => (src ? (src.startsWith("/") ? src : `/${src}`) : src);

/**
 * Builds a srcset string from the generated image manifest.
 * Falls back gracefully to the original path when no manifest entry exists.
 */
export function buildSrcSet(src, format) {
  const entry = images[key(src)];
  if (!entry) return undefined;
  return entry.variants.map((v) => `${v[format]} ${v.w}w`).join(", ");
}

export function getFallback(src) {
  return images[key(src)]?.fallback || src;
}

/**
 * Responsive <picture> with WebP + JPEG fallback, explicit dimensions
 * (prevents layout shift) and native lazy loading by default.
 */
const ResponsiveImage = ({
  src,
  alt,
  sizes = "100vw",
  className,
  loading = "lazy",
  fetchPriority,
  width,
  height,
  decoding = "async",
  ...rest
}) => {
  const entry = images[key(src)];
  const fallback = entry?.fallback || src;
  const webpSet = buildSrcSet(src, "webp");
  const jpgSet = buildSrcSet(src, "jpg");

  return (
    <picture>
      {webpSet && <source type="image/webp" srcSet={webpSet} sizes={sizes} />}
      <img
        src={fallback}
        srcSet={jpgSet}
        sizes={jpgSet ? sizes : undefined}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        width={width ?? entry?.width}
        height={height ?? entry?.height}
        {...rest}
      />
    </picture>
  );
};

export default ResponsiveImage;
